import { ROOT_PARENT_KEY } from "../../DisplayTree/search-utils";
import { Space } from "../tree-nodes";
import { SpaceSearchInput } from "./SearchBar";

export const matchesHierarchicalTerm = (
  space: Space,
  hierarchicalTerm: string,
  spacesByIds: Record<string, Space>,
  skipLevels = false,
): boolean => {
  const pathParts = hierarchicalTerm
    .split("<>")
    .map((part) => part.trim().toLowerCase());

  if (pathParts.length === 0) {
    return false;
  }

  const currentSpaceName = space.name.toLowerCase();
  const lastPart = pathParts[pathParts.length - 1];

  if (!currentSpaceName.includes(lastPart)) {
    return false;
  }

  if (pathParts.length === 1) {
    return true;
  }

  if (skipLevels) {
    return matchesHierarchicalTermWithSkipLevels(space, pathParts, spacesByIds);
  } else {
    return matchesHierarchicalTermExactPath(space, pathParts, spacesByIds);
  }
};

const matchesHierarchicalTermExactPath = (
  space: Space,
  pathParts: string[],
  spacesByIds: Record<string, Space>,
): boolean => {
  let currentSpace: Space | undefined = space;
  let pathIndex = pathParts.length - 2;

  while (currentSpace && pathIndex >= 0) {
    const parentId = currentSpace.parentId;
    const expectedPart = pathParts[pathIndex];

    if (!parentId || parentId === "root" || parentId === ROOT_PARENT_KEY) {
      break;
    }

    const parentSpace = spacesByIds[parentId];
    if (!parentSpace) {
      break;
    }

    const parentName = parentSpace.name.toLowerCase();

    if (!parentName.includes(expectedPart)) {
      return false;
    }

    currentSpace = parentSpace;
    pathIndex--;
  }

  return pathIndex < 0;
};

const matchesHierarchicalTermWithSkipLevels = (
  space: Space,
  pathParts: string[],
  spacesByIds: Record<string, Space>,
): boolean => {
  const currentSpaceName = space.name.toLowerCase();
  const lastPart = pathParts[pathParts.length - 1];

  if (!currentSpaceName.includes(lastPart)) {
    return false;
  }

  if (pathParts.length === 1) {
    return true;
  }

  return hasValidSkipLevelPath(space, pathParts, spacesByIds);
};

const hasValidSkipLevelPath = (
  space: Space,
  pathParts: string[],
  spacesByIds: Record<string, Space>,
): boolean => {
  let currentSpace: Space | undefined = space;
  let pathIndex = pathParts.length - 2;

  while (currentSpace && pathIndex >= 0) {
    const expectedPart = pathParts[pathIndex];

    const matchingAncestor = findMatchingAncestor(
      currentSpace,
      expectedPart,
      spacesByIds,
    );

    if (!matchingAncestor) {
      return false;
    }

    currentSpace = matchingAncestor;
    pathIndex--;
  }

  return pathIndex < 0;
};

const findMatchingAncestor = (
  space: Space,
  expectedPart: string,
  spacesByIds: Record<string, Space>,
): Space | null => {
  let currentSpace: Space | undefined = space;
  let depth = 0;
  const maxDepth = 100; // Prevent infinite loops

  while (currentSpace && depth < maxDepth) {
    depth++;
    const parentId = currentSpace.parentId;

    if (!parentId || parentId === "root" || parentId === ROOT_PARENT_KEY) {
      if (currentSpace.name.toLowerCase().includes(expectedPart)) {
        return currentSpace;
      }
      return null;
    }

    const parentSpace = spacesByIds[parentId];
    if (!parentSpace) {
      return null;
    }

    if (parentSpace.name.toLowerCase().includes(expectedPart)) {
      return parentSpace;
    }

    currentSpace = parentSpace;
  }

  return null;
};

export const isHierarchicalTerm = (term: string): boolean => {
  return term.includes("<>");
};

export const matches = (
  space: Space,
  searchInput: SpaceSearchInput,
  context?: Record<string, Space>,
) => {
  if (searchIsEmpty(searchInput)) {
    return false;
  }

  if (searchInput.categories.length > 0) {
    if (!space.category || !searchInput.categories.includes(space.category)) {
      return false;
    }
  }

  if (searchInput.spaceTypes.length > 0) {
    if (!space.type || !searchInput.spaceTypes.includes(space.type)) {
      return false;
    }
  }

  if (searchInput.terms.length > 0) {
    const hierarchicalTerms = searchInput.terms.filter(isHierarchicalTerm);
    const regularTerms = searchInput.terms.filter(
      (term) => !isHierarchicalTerm(term),
    );

    let hasAnyMatch = false;

    if (hierarchicalTerms.length > 0) {
      if (context) {
        const hierarchicalMatch = hierarchicalTerms.some((term) => {
          const result = matchesHierarchicalTerm(
            space,
            term,
            context,
            searchInput.skipLevels,
          );
          return result;
        });
        if (hierarchicalMatch) {
          hasAnyMatch = true;
        }
      }
    }

    if (regularTerms.length > 0) {
      const name = space.name.toLowerCase();
      const termMatch = regularTerms.some((term) => {
        const termMatches = name.includes(term.toLowerCase());
        return termMatches;
      });
      if (termMatch) {
        hasAnyMatch = true;
      }
    }

    // If no terms matched, return false
    if (!hasAnyMatch) {
      return false;
    }
  }

  if (searchInput.tags.length > 0) {
    if (!space.tags || space.tags.length === 0) {
      return false;
    }

    const lowerTags = space.tags.map((tag) => tag.toLowerCase());
    const tagMatch = searchInput.tags.some((tag) =>
      lowerTags.some((spaceTag) => spaceTag.includes(tag.toLowerCase())),
    );

    if (!tagMatch) {
      return false;
    }
  }

  return true;
};

export const searchIsEmpty = (input: SpaceSearchInput): boolean =>
  input.terms.length === 0 &&
  input.categories.length === 0 &&
  input.spaceTypes.length === 0 &&
  input.tags.length === 0;
